// src/pages/partner-admin/PartnerDashboardPage.jsx
import React, { useEffect, useState } from "react";
import { getPartnerDashboard, getPartnerCompetitions } from "@/services/partnerAdminService";
import { useNavigate } from "react-router-dom";

const STAT_CONFIG = [
  { key: "active_competitions",  label: "Live Competitions", accent: true },
  { key: "total_competitions",   label: "Total Competitions" },
  { key: "pending_approvals",    label: "Pending Approvals",  warn: true },
  { key: "total_submissions",    label: "Total Submissions" },
  { key: "total_users",          label: "Community Members" },
  { key: "total_votes",          label: "Total Votes" },
];

function StatCard({ label, value, accent, warn }) {
  return (
    <div className={"pa-stat" + (accent ? " pa-stat--accent" : "") + (warn && value > 0 ? " pa-stat--warn" : "")}>
      <span className="pa-stat__value">{value ?? "—"}</span>
      <span className="pa-stat__label">{label}</span>
    </div>
  );
}

function CompetitionRow({ comp, onClick }) {
  const statusColour = {
    open:     "#c8f04a",
    draft:    "#555",
    judging:  "#f0c84a",
    closed:   "#888",
    archived: "#333",
  }[comp.status] || "#555";

  return (
    <div className="pa-comp-row" onClick={onClick}>
      <div className="pa-comp-row__left">
        <span className="pa-comp-row__title">{comp.title}</span>
        <span className="pa-comp-row__meta">
          {comp.submission_count ?? 0} submissions
        </span>
      </div>
      <div className="pa-comp-row__right">
        <span className="pa-comp-row__status" style={{ color: statusColour }}>
          {comp.status}
        </span>
      </div>
    </div>
  );
}

export default function PartnerDashboardPage() {
  const [stats, setStats]             = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [statsData, compsData] = await Promise.all([
          getPartnerDashboard(),
          getPartnerCompetitions(),
        ]);
        setStats(statsData);
        setCompetitions(compsData.slice(0, 5)); // most recent 5
      } catch (err) {
        setError(err.message || "Failed to load dashboard.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="pa-page-loading">Loading...</div>;
  if (error)   return <div className="pa-page-error">{error}</div>;

  return (
    <div className="pa-dashboard">
      {/* Stats grid */}
      <section className="pa-dashboard__stats">
        {STAT_CONFIG.map(({ key, label, accent, warn }) => (
          <StatCard
            key={key}
            label={label}
            value={stats?.[key]}
            accent={accent}
            warn={warn}
          />
        ))}
      </section>

      {/* Recent competitions */}
      <section className="pa-dashboard__section">
        <div className="pa-section-header">
          <h2 className="pa-section-header__title">Recent Competitions</h2>
          <button
            className="pa-section-header__action"
            onClick={() => navigate("/partner-admin/competitions")}
          >
            View all →
          </button>
        </div>

        {competitions.length === 0 ? (
          <div className="pa-empty">
            <span>No competitions yet.</span>
            <button
              className="pa-btn pa-btn--primary"
              onClick={() => navigate("/partner-admin/competitions")}
            >
              Create your first competition
            </button>
          </div>
        ) : (
          <div className="pa-comp-list">
            {competitions.map(comp => (
              <CompetitionRow
                key={comp.id}
                comp={comp}
                onClick={() => navigate("/partner-admin/competitions", { state: { editId: comp.id } })}
              />
            ))}
          </div>
        )}
      </section>

      {/* Quick actions */}
      <section className="pa-dashboard__section">
        <div className="pa-section-header">
          <h2 className="pa-section-header__title">Quick Actions</h2>
        </div>
        <div className="pa-quick-actions">
          <button className="pa-quick-action" onClick={() => navigate("/partner-admin/competitions")}>
            <span className="pa-quick-action__icon">◈</span>
            <span>New Competition</span>
          </button>
          <button className="pa-quick-action" onClick={() => navigate("/partner-admin/submissions?approved=false")}>
            <span className="pa-quick-action__icon">◉</span>
            <span>Review Submissions</span>
            {stats?.pending_approvals > 0 && (
              <span className="pa-quick-action__badge">{stats.pending_approvals}</span>
            )}
          </button>
          <button className="pa-quick-action" onClick={() => navigate("/partner-admin/users")}>
            <span className="pa-quick-action__icon">◎</span>
            <span>View Members</span>
          </button>
        </div>
      </section>

      <style>{`
        .pa-dashboard { display: flex; flex-direction: column; gap: 32px; }

        /* Stats */
        .pa-dashboard__stats {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 12px;
        }
        .pa-stat {
          background: #141414;
          border: 1px solid #1e1e1e;
          border-radius: 8px;
          padding: 18px 20px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          transition: border-color 0.15s;
        }
        .pa-stat:hover { border-color: #2a2a2a; }
        .pa-stat--accent { border-color: #2a3a1a; background: #111a0d; }
        .pa-stat--warn   { border-color: #3a2a1a; background: #1a1109; }
        .pa-stat__value {
          font-size: 28px;
          font-weight: 700;
          color: #e8e8e8;
          letter-spacing: -0.02em;
          line-height: 1;
        }
        .pa-stat--accent .pa-stat__value { color: var(--colour-primary, #c8f04a); }
        .pa-stat--warn   .pa-stat__value { color: #f0a84a; }
        .pa-stat__label {
          font-size: 11px;
          color: #555;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        /* Section */
        .pa-dashboard__section { display: flex; flex-direction: column; gap: 12px; }
        .pa-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .pa-section-header__title {
          font-size: 13px;
          font-weight: 600;
          color: #888;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin: 0;
        }
        .pa-section-header__action {
          background: none;
          border: none;
          color: var(--colour-primary, #c8f04a);
          font-size: 12px;
          cursor: pointer;
          padding: 0;
        }
        .pa-section-header__action:hover { opacity: 0.8; }

        /* Competition list */
        .pa-comp-list {
          background: #111;
          border: 1px solid #1e1e1e;
          border-radius: 8px;
          overflow: hidden;
        }
        .pa-comp-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          border-bottom: 1px solid #1a1a1a;
          cursor: pointer;
          transition: background 0.1s;
        }
        .pa-comp-row:last-child { border-bottom: none; }
        .pa-comp-row:hover { background: #161616; }
        .pa-comp-row__left { display: flex; flex-direction: column; gap: 3px; }
        .pa-comp-row__title { font-size: 13px; color: #ccc; font-weight: 500; }
        .pa-comp-row__meta  { font-size: 11px; color: #444; }
        .pa-comp-row__status {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-weight: 600;
        }

        /* Empty state */
        .pa-empty {
          background: #111;
          border: 1px dashed #222;
          border-radius: 8px;
          padding: 32px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          color: #444;
          font-size: 13px;
        }

        /* Quick actions */
        .pa-quick-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .pa-quick-action {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #111;
          border: 1px solid #1e1e1e;
          border-radius: 7px;
          padding: 11px 16px;
          color: #aaa;
          font-size: 13px;
          cursor: pointer;
          transition: border-color 0.15s, color 0.15s;
          position: relative;
        }
        .pa-quick-action:hover {
          border-color: var(--colour-primary, #c8f04a);
          color: #e8e8e8;
        }
        .pa-quick-action__icon { font-size: 14px; color: var(--colour-primary, #c8f04a); }
        .pa-quick-action__badge {
          background: #f0a84a;
          color: #000;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 10px;
          margin-left: 2px;
        }

        /* Shared */
        .pa-btn {
          padding: 9px 18px;
          border-radius: 6px;
          font-size: 13px;
          cursor: pointer;
          border: none;
          font-weight: 500;
        }
        .pa-btn--primary {
          background: var(--colour-primary, #c8f04a);
          color: #0d0d0d;
        }
        .pa-page-loading { color: #555; font-size: 13px; padding: 40px 0; }
        .pa-page-error   { color: #f04a4a; font-size: 13px; padding: 40px 0; }
      `}</style>
    </div>
  );
}